import {
    CircularProgress,
    Step,
    StepLabel,
    Stepper,
    Typography,
} from "@material-ui/core";
import { LogRequest, LogRequestStatus } from "generated-interfaces/graphql";
import { MaterialTableProps } from "material-table";
import React, { useState, useEffect } from "react";
import CustomizedMaterialTable from "components/DataTable";
import { LOG_REQUEST_IN_PROGRESS_STATUSES } from "utils/constants";
import axios from "axios";

interface Data {
    timestamp: string;
    type: string;
    source: string;
    message: string;
}

export const SelectedLogRequest = ({
    logRequest,
}: {
    logRequest: LogRequest;
}) => {
    const [data, setData] = useState<Data[] | null>(null);
    const [dataLoading, setDataLoading] = useState<boolean>(false);

    useEffect(() => {
        if (
            logRequest.status === LogRequestStatus.Successful &&
            logRequest.outputFilePath
        ) {
            setDataLoading(true);
            axios
                .get<Data[]>(logRequest.outputFilePath)
                .then((response) => {
                    setData(response.data);
                })
                .finally(() => {
                    setDataLoading(false);
                });
        }
    }, [logRequest.status, logRequest.outputFilePath]);

    const dataConfig = (data: Data[]): MaterialTableProps<Data> => {
        return {
            columns: [
                { title: "Timestamp", field: "timestamp" },
                {
                    title: "Type",
                    field: "type",
                },
                {
                    title: "Source",
                    field: "source",
                },
                {
                    title: "Message",
                    field: "message",
                    sorting: false,
                },
            ],
            data: data.map((data) => ({ ...data })),
        };
    };

    const renderSteps = () => {
        const steps = ["Log upload", "Log parsing", "Parsed"];

        const activeStep: {
            [key in LogRequestStatus]: {
                step: number;
                type: "loading" | "error" | "success";
            };
        } = {
            [LogRequestStatus.NotIntiated]: {
                step: 0,
                type: "loading",
            },
            [LogRequestStatus.LogUploadInProgress]: {
                step: 0,
                type: "loading",
            },
            [LogRequestStatus.LogUploadFailed]: {
                step: 0,
                type: "error",
            },
            [LogRequestStatus.LogUploadSuccessful]: {
                step: 1,
                type: "success",
            },
            [LogRequestStatus.ProcessingUploadedLogs]: {
                step: 1,
                type: "loading",
            },
            [LogRequestStatus.ProcessingUploadedLogsFailed]: {
                step: 1,
                type: "error",
            },
            [LogRequestStatus.Successful]: {
                step: 3,
                type: "success",
            },
        };

        return (
            <div>
                <Stepper
                    activeStep={activeStep[logRequest.status].step}
                    alternativeLabel
                >
                    {steps.map((label, index) => {
                        const isActiveStep =
                            activeStep[logRequest.status].step === index;
                        const activeStepType =
                            isActiveStep && activeStep[logRequest.status].type;
                        return (
                            <Step key={label}>
                                <StepLabel
                                    error={activeStepType === "error"}
                                    disabled
                                    optional={
                                        activeStepType === "error" && (
                                            <Typography
                                                variant="caption"
                                                color="error"
                                            >
                                                {
                                                    logRequest.metadata
                                                        ?.failedReason
                                                }
                                            </Typography>
                                        )
                                    }
                                >
                                    {label}
                                </StepLabel>
                                {/* <StepContent>
                                    <Typography>{getStepContent(index)}</Typography>
                                </StepContent> */}
                            </Step>
                        );
                    })}
                </Stepper>

                {LOG_REQUEST_IN_PROGRESS_STATUSES.includes(
                    logRequest.status
                ) && (
                    <Typography align="center">
                        You will get email notification once completed.
                    </Typography>
                )}
            </div>
        );
    };

    return (
        <div>
            {renderSteps()}

            {dataLoading && <CircularProgress />}

            {!dataLoading &&
                logRequest.status === LogRequestStatus.Successful &&
                !data?.length && (
                    <Typography color="error" align="center">
                        No logs found
                    </Typography>
                )}

            {!!data?.length && (
                <CustomizedMaterialTable<Data> {...dataConfig(data)} />
            )}
        </div>
    );
};
